/// <reference path="../../setup/_references.ts" />
module onvio.practice.widgets {
    'use strict';

    /** Scope for practice widgets */
    export interface IBmPracticeWidgetScope extends ng.IScope {
        /** Name of the widget which uses convention to find the correct parts */
        widget: string;
        /** Contextual client or staff */
        context: cs.INamedReferenceWithCodeDto;
        /** Dashboard Owner ID */
        identifier: string;
        /** Optional type when widgets offer multiple configurations */
        type?: string;
        /** Controller */
        ctrl: IBmPracticeWidgetController;
        /** Set to false to hide the header */
        header?: string;
        /** Set to 'false' to hide the date selector on the header */
        dateSelector?: string;
        /** Set to 'false' to hide the search box on the header */
        searchBox?: string;
    }

    /** Standard widget sizes */
    export type WidgetSize = 'xxlarge' | 'xlarge' | 'large' | 'medium' | 'small' | 'xsmall' | 'xxsmall';

    /** Service that contains widget-specific logic */
    export interface IBmPracticeWidgetService {
        /** Service is busy processing */
        isBusy: boolean;
        /** Custom initalization */
        initialize?: (widget: IBmPracticeWidgetController, done: () => void) => void;
        /** Loads the data */
        loadData: (args: IBmPracticeWidgetLoadDataArgs) => ng.IPromise<any>;
        /** Location of the template for the body of the widget */
        bodyTemplate: string;
        /** Location of the template for the header of the widget */
        headerTemplate?: string;
        /** Allows multiple widgets to coalesce in a data area to ensure data concurrency */
        coalescingServiceArea?: string;
        /** Refreshes the data - this is added by bm-practice-widget during construction */
        refreshData?: (dataChanged?: boolean) => ng.IPromise<any>;
        /** Set to false to hide the date selector on the header */
        dateSelector?: boolean;
        /** Set to false to hide the search box on the header */
        searchBox?: boolean;
        /** Custom consistent sizing of widgets */
        size?: WidgetSize;
    }

    /** Maintains data refreshes of coalescing services */
    export interface IBmPracticeWidgetCoalesceService {
        /** Refreshes any other coalescing widgets */
        refreshAllData: (service: IBmPracticeWidgetService) => void;
        /** Registers a widget service for coalescing widget */
        registerCoalesceService: (service: IBmPracticeWidgetService) => void;
        /** Un-Registers a widget service for coalescing widget */
        unregisterCoalesceService: (service: IBmPracticeWidgetService) => void;
    }

    /** Standard arguments for loading data */
    export interface IBmPracticeWidgetLoadDataArgs {
        /** Dashboard owner id e.g. client or staff */
        contextId?: string;
        /** Dashboard owner */
        context?: cs.INamedReferenceWithCodeDto;
        /** Optional type when widgets offer multiple configurations */
        type?: string;
        // Date range
        dateRangeId?: string;
        startDate?: string;
        endDate?: string;
        // Search
        searchText?: string;
        // Pagination
        pageIndex?: number;
    }

    /** Controller used as a container for all widgets */
    export interface IBmPracticeWidgetController {
        /** Flag if the component is busy processing or loading data */
        isBusy: boolean;
        /** Flag if there is data being loaded */
        isLoadingData: boolean;
        /** Flag if there is a header */
        hasHeader: boolean;
        /** Default options for date range selector */
        dateRangeSelectorOptions: any[];
        /** Default selected option for date range selector */
        dateRangeSelectorDefault: string;
        /** Service that contains the widget specific logic */
        service: IBmPracticeWidgetService;
        /** Arguments for loading data */
        loadDataArgs: IBmPracticeWidgetLoadDataArgs;
        /** Data for the widget */
        data: any;
        /** Loads more data */
        loadMoreData(): ng.IPromise<any>;
        /** Flag if there is more data to load */
        hasMoreData: boolean;
        /** Initializes the header (can be re-run after modification) */
        initializeHeader(): void;
    }

    export class BmPracticeWidgetCoalesceService implements IBmPracticeWidgetCoalesceService {

        // This will be an object with properties that contain arrays of services
        private services: any = {};

        static $inject: string[] = [];
        constructor() {
            // Nothing required yet
        }

        public refreshAllData(service: IBmPracticeWidgetService): void {
            if (this.services[service.coalescingServiceArea]) {
                _.forEach(this.services[service.coalescingServiceArea], (coalescingService: IBmPracticeWidgetService) => {
                    if (coalescingService !== service) {
                        // Refresh the other services, but don't propogate the changed data flag
                        coalescingService.refreshData(false);
                    }
                });
            }
        }

        public registerCoalesceService(service: IBmPracticeWidgetService): void {
            if (!this.services[service.coalescingServiceArea]) {
                this.services[service.coalescingServiceArea] = [];
            }
            this.services[service.coalescingServiceArea].push(service);
        }

        public unregisterCoalesceService(service: IBmPracticeWidgetService): void {
            if (this.services[service.coalescingServiceArea]) {
                this.services[service.coalescingServiceArea] = _.filter(this.services[service.coalescingServiceArea], (coalescingService: IBmPracticeWidgetService) => service !== coalescingService);
            }
        }

    }

    export class BmPracticeWidgetController implements IBmPracticeWidgetController {

        // IBmPracticeWidgetController
        public isBusy: boolean = false;
        public isLoadingData: boolean = true;
        public currentDataPromise: ng.IPromise<any>;
        public hasHeader: boolean = false;
        public hasDateSelector: boolean = false;
        public hasSearchBox: boolean = false;
        public service: IBmPracticeWidgetService = undefined;
        public loadDataArgs: IBmPracticeWidgetLoadDataArgs = undefined;
        public data: any = undefined;
        public hasMoreData: boolean = false;
        public css: any = {};

        // Default date range options for the standard header
        public dateRangeSelectorOptions: any = [
            { id: 'ALL' },
            { id: 'TODAY' },
            { id: 'WEEK' },
            { id: 'TWOWEEKS' },
            { id: 'MONTH' },
            { id: 'CUSTOM' }
        ];
        public dateRangeSelectorDefault = 'ALL';

        static $inject = [
            '$scope',
            '$attrs',
            '$q',
            '$templateCache',
            '$injector',
            'bmPracticeWidgetCoalesceService'
        ];
        constructor(
            public $scope: IBmPracticeWidgetScope,
            public $attrs: ng.IAttributes,
            private $q: ng.IQService,
            private $templateCache: ng.ITemplateCacheService,
            private $injector: ng.auto.IInjectorService,
            private coalesceService: IBmPracticeWidgetCoalesceService) {

            // Load the widget specific service
            if (this.$injector.has('bm-practice-widget-' + this.$scope.widget)) {
                this.service = <any>this.$injector.get('bm-practice-widget-' + this.$scope.widget);
                // If the service wants to coalesce with others then register it
                if (this.service.coalescingServiceArea) {
                    this.coalesceService.registerCoalesceService(this.service);
                    // And ensure it unregisters when its destroyed
                    let thisService = this.service;
                    this.$scope.$on('$destroy', () => {
                        this.coalesceService.unregisterCoalesceService(thisService);
                    });
                }
                this.service.refreshData = (dataChanged: boolean = false) => {
                    if (this.service.coalescingServiceArea && dataChanged) {
                        this.coalesceService.refreshAllData(this.service);
                    }
                    this.loadDataArgs.pageIndex = 1;
                    return this._loadData();
                };
                // Allow custom consistent sizing
                this.css['bm-practice-widget-' + (this.service.size || 'medium')] = true;
            } else {
                console.error('Unknown practice widget "' + this.$scope.widget + '" - ensure you have a service named "bm-practice-widget-' + this.$scope.widget + '"');
                return;
            }

            // Provide an opportunity for the widget to provide custom initialization
            if (!!this.service.initialize) {
                this.service.initialize(this, this.initializeHeader.bind(this));
            } else {
                this.initializeHeader();
            }

            // The isBusy property takes into account the service being busy as well as data loading
            Object.defineProperty(this, 'isBusy', {
                get: () => {
                    return this.isLoadingData || this.service.isBusy;
                }
            });

            this.$scope.$watch('context', (context: cs.INamedReferenceDto) => {
                if (!!context) {
                    this.loadDataArgs = _.extend(angular.copy(this.loadDataArgs || { type: this.$scope.type }), {
                        context: this.$scope.context,
                        contextId: this.$scope.context.id,
                        pageIndex: 1
                    });
                    // If the date selector is hidden then force to show all
                    if (!this.hasDateSelector) {
                        this.loadDataArgs.dateRangeId = 'ALL';
                    }
                    this._loadData();
                }
            });

        }

        /** Initializes the header */
        public initializeHeader(): void {
            // Initialize the widget and allow optional hiding of the header
            this.hasHeader = !!this.$templateCache.get(this.service.headerTemplate) && this.$scope.header !== 'false';
            this.hasDateSelector = this.hasHeader && this.$scope.dateSelector !== 'false' && this.service.dateSelector !== false;
            this.hasSearchBox = this.hasHeader && this.$scope.searchBox !== 'false' && this.service.searchBox !== false && this.canSearchBoxFit();
        }

        public canSearchBoxFit(): boolean {
            if ((this.service.size.indexOf('small') > -1)) {
                if (this.loadDataArgs && this.loadDataArgs.dateRangeId !== 'ALL') {
                    return false;
                }
            }
            return true;
        }

        /** Applies a date range to the data */
        public applyDateRange(dateRangeId: string, startDate: string, endDate: string): ng.IPromise<any> {
            this.loadDataArgs = this.loadDataArgs || { type: this.$scope.type };
            this.loadDataArgs.dateRangeId = dateRangeId;
            this.loadDataArgs.startDate = dateRangeId === 'ALL' ? undefined : startDate;
            this.loadDataArgs.endDate = dateRangeId === 'ALL' ? undefined : endDate;
            this.loadDataArgs.pageIndex = 1;
            this.initializeHeader();
            return this._loadData();
        }

        /** Applies a search to the data */
        public applySearch(searchText: string): ng.IPromise<any> {
            if (searchText !== this.loadDataArgs.searchText) {
                this.loadDataArgs.searchText = searchText;
                this.loadDataArgs.pageIndex = 1;
                return this._loadData();
            } else {
                return this.$q.when(this.data);
            }
        }

        /** Loads more data */
        public loadMoreData(): ng.IPromise<any> {
            if (this.hasMoreData) {
                this.loadDataArgs.pageIndex++;
                return this._loadData();
            } else {
                return this.$q.when(this.data);
            }
        }

        private _canLoadData() {
            return (!this.$attrs.context || this.$scope.context) && (!this.hasDateSelector || this.loadDataArgs.dateRangeId);
        }

        /** Requests the loading of data */
        public _loadData() {
            this.isLoadingData = true;
            if (!this._canLoadData()) {
                return;
            }
            let promise = this.service.loadData(this.loadDataArgs).then((data: any) => {
                if (this.currentDataPromise !== promise) {
                    return;
                }
                if (data && data.pageIndex && data.items) {
                    // Data list
                    if (this.loadDataArgs.pageIndex === data.pageIndex) {
                        // Sent the same page back as was requested so we can trust the data
                        if (this.loadDataArgs.pageIndex > 1 && this.data.items && data.items) {
                            // This isn't the first page so we need to append the data
                            _.forEach(data.items, (item) => this.data.items.push(item));
                        } else {
                            // This is the first page so we can replace the data
                            this.data = data;
                        }
                        this.hasMoreData = data.pageIndex < data.totalPages;
                    } else {
                        // No further data was available
                        this.loadDataArgs.pageIndex = data.pageIndex;
                        this.hasMoreData = false;
                    }
                } else {
                    // Data view or single item
                    this.data = data;
                    this.hasMoreData = false;
                }
                this.isLoadingData = false;
                return data;
            });
            this.currentDataPromise = promise;
            return promise;
        }

    }

    /* Directive for the widget wrapper */
    angular.module('bm.practice.widgets', [
        'bm.practice.widgets.scrollingContainer',
        // List of defined widgets
        'bm.practice.widgets.priorInvoices',
        'bm.practice.widgets.accountsReceivableOpen',
        'bm.practice.widgets.agings',
        'bm.practice.widgets.keyTotals',
        'bm.practice.widgets.accountsReceivableSummary',
        'bm.practice.widgets.wipSummary',
        'bm.practice.widgets.timeSummary',
        'bm.practice.widgets.myAssignments'
    ])
        .controller('bmPracticeWidgetController', BmPracticeWidgetController)
        .service('bmPracticeWidgetCoalesceService', BmPracticeWidgetCoalesceService)
        .directive('bmPracticeWidget', [(): ng.IDirective => {
            return {
                restrict: 'E',
                require: 'bmPracticeWidget',
                scope: {
                    widget: '@',
                    context: '=',
                    identifier: '@?',
                    type: '@?',
                    header: '@?',
                    dateSelector: '@?',
                    searchBox: '@?'
                },
                controller: 'bmPracticeWidgetController',
                controllerAs: 'ctrl',
                templateUrl: 'practice-widgets/bm-practice-widget/bm-practice-widget.html',
                link: (scope: IBmPracticeWidgetScope, elem: ng.IAugmentedJQuery, attr: ng.IAttributes, ctrl: BmPracticeWidgetController) => {
                    /** Nothing yet */
                }
            };
        }]);
}
